/**
 * Local subscription / tier management.
 * All state stored in IndexedDB alongside the user profile.
 * No real payment processing — this is the data model for when you add it.
 */

import { getProfile, updateProfile } from "../lib/auth";

export interface SubscriptionPlan {
  id: "free" | "pro" | "scholar";
  name: string;
  nameKey: string;
  price: number;  // monthly, USD
  description: string;
  descriptionKey: string;
  maxCharts: number;
  features: string[];
  featureKeys: string[];
  color: string;
}

export const PLANS: SubscriptionPlan[] = [
  {
    id: "free",
    name: "Free",
    nameKey: "ui.web.subscription.plan.free.name",
    price: 0,
    description: "Core natal chart reading",
    descriptionKey: "ui.web.subscription.plan.free.description",
    maxCharts: 5,
    color: "text-parchment-400",
    features: [
      "Cast any chart + full natal Face, Council, Sound, Sky",
      "All past ages & yesterday's verified reading",
      "World sky, Mundane, Library, Discoveries",
      "Demo lives fully unlocked (proof + sales)",
      "Benchmark transparency report",
      "Local-only storage",
    ],
    featureKeys: [
      "ui.web.subscription.plan.free.feature_1",
      "ui.web.subscription.plan.free.feature_2",
      "ui.web.subscription.plan.free.feature_3",
      "ui.web.subscription.plan.free.feature_4",
      "ui.web.subscription.plan.free.feature_5",
      "ui.web.subscription.plan.free.feature_6",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    nameKey: "ui.web.subscription.plan.pro.name",
    price: 7,
    description: "Today and everything after",
    descriptionKey: "ui.web.subscription.plan.pro.description",
    maxCharts: 50,
    color: "text-brass-300",
    features: [
      "Your day, decoded (personalized Today feed)",
      "Current + future ages on the Movement ledger",
      "Future event windows & health outlook",
      "Unlimited Ask (horary) after free taste",
      "Full-length Life Music & Transit Radio",
      "50 saved charts",
      "Core engine stays rule-based",
    ],
    featureKeys: [
      "ui.web.subscription.plan.pro.feature_1",
      "ui.web.subscription.plan.pro.feature_2",
      "ui.web.subscription.plan.pro.feature_3",
      "ui.web.subscription.plan.pro.feature_4",
      "ui.web.subscription.plan.pro.feature_5",
      "ui.web.subscription.plan.pro.feature_6",
      "ui.web.subscription.plan.pro.feature_7",
    ],
  },
  {
    id: "scholar",
    name: "Scholar",
    nameKey: "ui.web.subscription.plan.scholar.name",
    price: 15,
    description: "Pro + deep research tools",
    descriptionKey: "ui.web.subscription.plan.scholar.description",
    maxCharts: 999,
    color: "text-lapis-300",
    features: [
      "Everything in Pro",
      "Multi-tradition explorer",
      "Birth time rectification",
      "Firdaria, Chara & Yogini dashas",
      "Unlimited saved charts",
      "API access (when billing lands)",
    ],
    featureKeys: [
      "ui.web.subscription.plan.scholar.feature_1",
      "ui.web.subscription.plan.scholar.feature_2",
      "ui.web.subscription.plan.scholar.feature_3",
      "ui.web.subscription.plan.scholar.feature_4",
      "ui.web.subscription.plan.scholar.feature_5",
      "ui.web.subscription.plan.scholar.feature_6",
    ],
  },
];

export function getPlan(id: string): SubscriptionPlan {
  return PLANS.find((p) => p.id === id) ?? PLANS[0];
}

export async function getCurrentTier(): Promise<"free" | "pro" | "scholar"> {
  const profile = await getProfile();
  return profile?.tier ?? "free";
}

export async function setTier(tier: "free" | "pro" | "scholar"): Promise<void> {
  // In production, this would verify payment first
  // For now, it's a local toggle
  await updateProfile({ tier });
}

export async function getChartLimit(): Promise<number> {
  const tier = await getCurrentTier();
  return getPlan(tier).maxCharts;
}

export async function canSaveChart(currentCount: number): Promise<boolean> {
  const limit = await getChartLimit();
  return currentCount < limit;
}
